import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Select,
  MenuItem,
  IconButton,
  Typography,
  CircularProgress,
  Button,
  FormControl,
  FormHelperText,
} from '@mui/material'
import Delete from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'
import { AssignmentViewModel, ToolRoleViewModel } from '@rsmus/ecp-userservice'
import api from '../../api'
import { tokens } from '../../styles/materialTheme'
import { Styles } from '../../types'

export interface UserAssignmentProps {
  userEmail: string
  onChange: (assignments: AssignmentViewModel[]) => void
  showValidationErrors?: boolean
  onValidityChange?: (isValid: boolean) => void
  loadExistingAssignments?: boolean
}

const defaultAssignment: AssignmentViewModel = {
  assignmentId: undefined,
  toolId: '',
  roleId: '',
}

const createEmptyAssignment = (): AssignmentViewModel => ({
  ...defaultAssignment,
})

const styles: Styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    width: '100%',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '2rem',
  },
  errorContainer: {
    padding: '1rem',
    backgroundColor: tokens.colors.warning.secondary,
    borderRadius: '0.25rem',
    marginBottom: '1rem',
  },
  errorText: {
    color: tokens.colors.rsmRed.secondary,
    fontFamily: 'Prelo',
    fontSize: '0.875rem',
  },
  assignmentRow: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
    flexDirection: { xs: 'column', sm: 'row' },
    marginBottom: '1rem',
    // Align with the two-column layout above and
    // reserve space on the right for the delete column.
    position: 'relative',
    paddingRight: { xs: 0, sm: '3rem' },
  },
  dropdownContainer: { flex: 1, minWidth: { xs: '100%', sm: '200px' } },
  removeButtonContainer: {
    display: 'flex',
    alignItems: { xs: 'flex-end', sm: 'center' },
    paddingTop: { xs: 0, sm: 0 },
    marginTop: { xs: '0.5rem', sm: 0 },
    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
    // On small screens and up, float the delete button
    // into its own visual column and vertically center it
    // relative to the Tool/Role fields.
    position: { xs: 'static', sm: 'absolute' },
    right: { xs: 'auto', sm: 0 },
    top: { xs: 'auto', sm: '50%' },
    transform: { xs: 'none', sm: 'translateY(-50%)' },
  },
  addButtonContainer: {
    marginTop: '0.5rem',
    justifyContent: 'center',
    display: 'flex',
  },
  addButton: {
    textTransform: 'none',
    fontFamily: 'Prelo',
    color: tokens.colors.rsmGreen.primary,
    padding: 0,
    '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
  },
  retryButton: { marginTop: '1rem' },
  validationError: {
    marginTop: '0.25rem',
    color: tokens.colors.rsmRed.secondary,
    fontFamily: 'Prelo',
    fontSize: '0.875rem',
  },
  labelText: {
    fontFamily: 'Prelo-Black, sans-serif',
    fontSize: '1rem',
    paddingBottom: '0.5rem',
    color: 'text.primary',
    display: 'block',
  },
}

const UserAssignment = ({
  userEmail,
  onChange,
  showValidationErrors = false,
  onValidityChange,
  loadExistingAssignments = true,
}: UserAssignmentProps) => {
  const { t } = useTranslation()

  const [assignments, setAssignments] = useState<AssignmentViewModel[]>([])
  const [availableTools, setAvailableTools] = useState<ToolRoleViewModel[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [duplicateError, setDuplicateError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<{
    [key: number]: { tool?: string; role?: string }
  }>({})

  const unwrapData = <T,>(response: any): T[] => {
    if (Array.isArray(response)) return response
    if (response?.data && Array.isArray(response.data)) return response.data
    return []
  }

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    setDuplicateError(null)
    setValidationErrors({})

    try {
      const toolsResponse = await api.user.clientAdmin_GetTools()

      const toolsData = unwrapData<ToolRoleViewModel>(
        (toolsResponse as { data?: ToolRoleViewModel[] })?.data ??
          toolsResponse,
      )

      setAvailableTools(
        toolsData.map((tool) => ({
          toolId: tool.toolId ?? '',
          toolName: tool.toolName ?? '',
          roleId: tool.roleId ?? '',
          roleName: tool.roleName ?? '',
        })),
      )

      if (loadExistingAssignments && userEmail) {
        const userAssignmentsResponse =
          await api.user.clientAdmin_GetUserAssignments(userEmail)

        const assignmentsData = unwrapData<AssignmentViewModel>(
          (userAssignmentsResponse as { data?: AssignmentViewModel[] })?.data ??
            userAssignmentsResponse,
        )

        setAssignments(
          assignmentsData.length > 0
            ? assignmentsData
            : [createEmptyAssignment()],
        )
      } else {
        setAssignments([createEmptyAssignment()])
      }
    } catch (err: unknown) {
      const errorMessage = t('UserManagement.AddUser.ErrorFetchingData')

      setError(errorMessage)
      setAssignments([createEmptyAssignment()])
    } finally {
      setIsLoading(false)
    }
  }, [userEmail, loadExistingAssignments, t])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const buildValidationErrors = useCallback(
    (includeEmptyRows: boolean) => {
      const errors: { [key: number]: { tool?: string; role?: string } } = {}

      assignments.forEach((assignment, index) => {
        if (!assignment.toolId) {
          if (includeEmptyRows) {
            errors[index] = {
              ...(errors[index] ?? {}),
              tool: t('UserManagement.AddUser.ToolRequired'),
            }
          }
          return
        }

        if (!assignment.roleId) {
          errors[index] = {
            ...(errors[index] ?? {}),
            role: t('UserManagement.AddUser.RoleRequiredWithTool'),
          }
        }
      })

      setValidationErrors(errors)
      return errors
    },
    [assignments, t],
  )

  useEffect(() => {
    const hasMissingTool =
      assignments.length === 0 || assignments.some((a) => !a.toolId)
    const hasMissingRole = assignments.some((a) => a.toolId && !a.roleId)
    const hasErrors = hasMissingTool || hasMissingRole || !!duplicateError

    onValidityChange?.(!hasErrors)
  }, [assignments, duplicateError, onValidityChange])

  useEffect(() => {
    if (showValidationErrors) {
      buildValidationErrors(true)
    }
  }, [showValidationErrors, assignments, buildValidationErrors])

  useEffect(() => {
    const validAssignments = assignments.filter((a) => a.toolId && a.roleId)
    onChange(validAssignments)
  }, [assignments, onChange])

  const getUniqueTools = () => {
    const map = new Map<string, string>()
    availableTools.forEach((tool) => {
      const id = tool.toolId ?? ''
      const name = tool.toolName ?? ''
      if (!map.has(id)) map.set(id, name)
    })

    return Array.from(map.entries())
      .map(([toolId, toolName]) => ({ toolId, toolName }))
      .sort((a, b) => a.toolName.localeCompare(b.toolName))
  }

  const getRolesForTool = (
    toolId: string,
  ): { roleId: string; roleName: string }[] => {
    const roles = availableTools
      .filter((tool) => (tool.toolId ?? '') === toolId)
      .map((tool) => ({
        roleId: tool.roleId ?? '',
        roleName: tool.roleName ?? '',
      }))
      .sort((a, b) => (a.roleName ?? '').localeCompare(b.roleName ?? ''))

    // Deduplicate roles in case the API returns duplicates for the same tool.
    const roleMap = new Map<string, string>()
    roles.forEach((role) => {
      if (!roleMap.has(role.roleId)) {
        roleMap.set(role.roleId, role.roleName)
      }
    })

    return Array.from(roleMap.entries()).map(([roleId, roleName]) => ({
      roleId,
      roleName,
    }))
  }

  const isToolRoleSelectedElsewhere = (
    toolId: string,
    roleId: string,
    currentIndex: number,
  ) =>
    assignments.some(
      (assignment, index) =>
        index !== currentIndex &&
        assignment.toolId === toolId &&
        assignment.roleId === roleId,
    )

  const hasAvailableRoleForTool = (toolId: string, currentIndex: number) => {
    const roles = getRolesForTool(toolId)
    if (roles.length === 0) return false

    return roles.some((role) => {
      const isCurrentSelection =
        assignments[currentIndex]?.toolId === toolId &&
        assignments[currentIndex]?.roleId === role.roleId

      if (isCurrentSelection) return true

      return !isToolRoleSelectedElsewhere(toolId, role.roleId, currentIndex)
    })
  }

  const isRoleOptionDisabled = (
    toolId: string,
    roleId: string,
    currentIndex: number,
  ) => {
    if (!toolId || !roleId) return false

    const isCurrentSelection =
      assignments[currentIndex]?.toolId === toolId &&
      assignments[currentIndex]?.roleId === roleId

    if (isCurrentSelection) return false

    return isToolRoleSelectedElsewhere(toolId, roleId, currentIndex)
  }

  const isToolOptionDisabled = (toolId: string, currentIndex: number) => {
    // Always allow the currently selected tool to stay enabled in its own row.
    if (assignments[currentIndex]?.toolId === toolId) return false

    return !hasAvailableRoleForTool(toolId, currentIndex)
  }

  const handleToolChange = (index: number, toolId: string) => {
    if (toolId && isToolOptionDisabled(toolId, index)) return

    const newAssignments = [...assignments]
    newAssignments[index] = { ...newAssignments[index], toolId, roleId: '' }
    setAssignments(newAssignments)

    const errors = { ...validationErrors }
    if (errors[index]) {
      delete errors[index].tool
      delete errors[index].role
      if (Object.keys(errors[index]).length === 0) delete errors[index]
    }
    setValidationErrors(errors)
  }

  const handleRoleChange = (index: number, roleId: string) => {
    const toolId = assignments[index]?.toolId ?? ''

    if (isRoleOptionDisabled(toolId, roleId, index)) {
      setDuplicateError(t('UserManagement.AddUser.DuplicateAssignment'))
      return
    }

    const newAssignments = [...assignments]
    newAssignments[index] = { ...newAssignments[index], roleId }
    setAssignments(newAssignments)

    const errors = { ...validationErrors }
    if (errors[index]) {
      delete errors[index].role
      if (Object.keys(errors[index]).length === 0) delete errors[index]
    }
    setValidationErrors(errors)
  }

  const handleRemoveAssignment = (index: number) => {
    const newAssignments = assignments.filter((_, i) => i !== index)

    if (newAssignments.length === 0)
      newAssignments.push(createEmptyAssignment())

    setAssignments(newAssignments)
    setDuplicateError(null)

    const errors = { ...validationErrors }
    if (errors[index]) delete errors[index]
    setValidationErrors(errors)
  }

  const handleAddAnotherTool = () => {
    const errors = buildValidationErrors(false)

    if (Object.keys(errors).length > 0) {
      return
    }

    setAssignments([...assignments, createEmptyAssignment()])
  }

  const handleRetry = async () => {
    await fetchData()
  }

  useEffect(() => {
    const seen = new Set<string>()
    let hasDuplicate = false

    assignments.forEach((assignment) => {
      if (!assignment.toolId || !assignment.roleId) return
      const key = `${assignment.toolId}-${assignment.roleId}`
      if (seen.has(key)) {
        hasDuplicate = true
      } else {
        seen.add(key)
      }
    })

    const newError = hasDuplicate
      ? t('UserManagement.AddUser.DuplicateAssignment')
      : null

    setDuplicateError((prev) => (prev === newError ? prev : newError))
  }, [assignments, t])

  const shouldShowAddButton = () =>
    assignments.length > 0 && assignments[0].toolId !== ''

  if (isLoading)
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    )

  if (error)
    return (
      <Box sx={styles.container}>
        <Box sx={styles.errorContainer} role="alert">
          <Typography sx={styles.errorText}>{error}</Typography>
          <Button
            variant="outlined"
            onClick={handleRetry}
            sx={styles.retryButton}
            data-testid="Btn-RetryFetchData">
            {t('UserManagement.AddUser.RetryButton')}
          </Button>
        </Box>
      </Box>
    )

  const uniqueTools = getUniqueTools()

  return (
    <Box sx={styles.container}>
      {duplicateError && (
        <Box role="alert" aria-live="polite">
          <Typography sx={styles.validationError}>{duplicateError}</Typography>
        </Box>
      )}

      {assignments.map((assignment, index) => (
        <Box
          key={
            assignment.assignmentId ||
            `assignment-${index}-${assignment.toolId || 'empty'}`
          }
          sx={styles.assignmentRow}>
          {/* TOOL LABEL + SELECT */}
          <Box sx={styles.dropdownContainer}>
            <Typography
              component="label"
              sx={styles.labelText}
              id={`tool-label-${index}`}
              htmlFor={`tool-select-${index}`}>
              {`${t('UserManagement.AddUser.ToolLabel')} *`}
              <span className="sr-only">{t('srOnlyRequired')}</span>
            </Typography>

            <FormControl
              fullWidth
              error={
                !!validationErrors[index]?.tool || !!validationErrors[index]
              }>
              <Select
                id={`tool-select-${index}`}
                labelId={`tool-label-${index}`}
                sx={{ marginBottom: '0.35rem' }}
                value={assignment.toolId}
                onChange={(e) => handleToolChange(index, e.target.value)}
                aria-label="Tool"
                data-testid={`Tool-dropdown-${index}`}
                MenuProps={{
                  disablePortal: true,
                  PaperProps: {
                    sx: { zIndex: 2000 },
                  },
                }}>
                <MenuItem value="">
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    {t('UserManagement.AddUser.SelectTool')}
                  </Typography>
                </MenuItem>

                {uniqueTools.map((tool) => {
                  const isDisabled = isToolOptionDisabled(tool.toolId, index)

                  return (
                    <MenuItem
                      key={tool.toolId}
                      value={tool.toolId}
                      disabled={isDisabled}>
                      {tool.toolName}
                    </MenuItem>
                  )
                })}
              </Select>
              {validationErrors[index]?.tool && (
                <FormHelperText>{validationErrors[index]?.tool}</FormHelperText>
              )}
            </FormControl>
          </Box>

          {/* ROLE LABEL + SELECT */}
          <Box sx={styles.dropdownContainer}>
            <Typography
              component="label"
              sx={styles.labelText}
              id={`role-label-${index}`}
              htmlFor={`role-select-${index}`}>
              {`${t('UserManagement.AddUser.RoleLabel')} *`}
              <span className="sr-only">{t('srOnlyRequired')}</span>
            </Typography>

            <FormControl
              fullWidth
              disabled={!assignment.toolId}
              error={!!validationErrors[index]?.role}>
              <Select
                id={`role-select-${index}`}
                labelId={`role-label-${index}`}
                sx={{ marginBottom: '0.35rem' }}
                value={assignment.roleId}
                onChange={(e) => handleRoleChange(index, e.target.value)}
                aria-label="Role"
                disabled={!assignment.toolId}
                data-testid={`Role-dropdown-${index}`}
                MenuProps={{
                  disablePortal: true,
                  PaperProps: {
                    sx: { zIndex: 2000 },
                  },
                }}>
                <MenuItem value="">
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    {t('UserManagement.AddUser.SelectRole')}
                  </Typography>
                </MenuItem>

                {getRolesForTool(assignment.toolId ?? '').map((role) => {
                  const isDisabled = isRoleOptionDisabled(
                    assignment.toolId ?? '',
                    role.roleId,
                    index,
                  )

                  return (
                    <MenuItem
                      key={role.roleId}
                      value={role.roleId}
                      disabled={isDisabled}>
                      {role.roleName}
                    </MenuItem>
                  )
                })}
              </Select>

              {validationErrors[index]?.role && (
                <FormHelperText>{validationErrors[index]?.role}</FormHelperText>
              )}
            </FormControl>
          </Box>

          {/* REMOVE BUTTON */}
          <Box sx={styles.removeButtonContainer}>
            <IconButton
              onClick={() => handleRemoveAssignment(index)}
              disabled={assignments.length === 1}
              color="error"
              aria-label={t('UserManagement.AddUser.RemoveAssignment')}>
              <Delete />
            </IconButton>
          </Box>
        </Box>
      ))}

      {shouldShowAddButton() && (
        <Box sx={styles.addButtonContainer}>
          <Button
            variant="text"
            onClick={handleAddAnotherTool}
            data-testid="Btn-AddAnotherTool"
            sx={styles.addButton}
            startIcon={<span>+</span>}>
            {t('UserManagement.AddUser.AddAnotherTool')}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default UserAssignment
